import { bytesToHex, hexToBytes } from "@noble/hashes/utils";

const STORAGE_KEY = "litstrudel-passkeys";
const PRF_SALT = new TextEncoder().encode("litstrudel-passkey-v1");
const HKDF_INFO = new TextEncoder().encode("litstrudel-passkey-aes");

export interface PasskeyRegistration {
  pubkey: string;
  credentialId: string; // base64url
  encryptedKey: string; // base64
  iv: string; // base64
  salt: string; // base64
  label: string;
  createdAt: string;
}

// --- Storage helpers ---

export function getPasskeyRegistrations(): PasskeyRegistration[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveRegistrations(regs: PasskeyRegistration[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(regs));
}

export function savePasskeyRegistration(reg: PasskeyRegistration) {
  const regs = getPasskeyRegistrations();
  regs.push(reg);
  saveRegistrations(regs);
}

export function removePasskeyRegistration(credentialId: string) {
  saveRegistrations(getPasskeyRegistrations().filter((r) => r.credentialId !== credentialId));
}

// --- Feature detection ---

export function isPasskeySupported(): boolean {
  return typeof window.PublicKeyCredential !== "undefined";
}

// --- Base64url helpers ---

function toBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(str: string): Uint8Array {
  const padded = str.replace(/-/g, "+").replace(/_/g, "/") + "==".slice(0, (4 - (str.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function fromBase64(str: string): Uint8Array {
  const binary = atob(str);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// --- Crypto helpers ---

function toBuffer(data: ArrayBuffer | Uint8Array | BufferSource): ArrayBuffer {
  if (data instanceof ArrayBuffer) return data;
  if (ArrayBuffer.isView(data)) return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
  return data as ArrayBuffer;
}

async function deriveAesKey(prfOutput: ArrayBuffer, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey("raw", prfOutput, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: toBuffer(salt), info: toBuffer(HKDF_INFO) },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

async function encryptSecretKey(secretKey: Uint8Array, prfOutput: ArrayBuffer): Promise<{ ct: string; iv: string; salt: string }> {
  const salt = crypto.getRandomValues(new Uint8Array(32));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const aesKey = await deriveAesKey(prfOutput, salt);
  const plaintext = new TextEncoder().encode(bytesToHex(secretKey));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv: toBuffer(iv) }, aesKey, plaintext);
  return { ct: toBase64(ciphertext), iv: toBase64(toBuffer(iv)), salt: toBase64(toBuffer(salt)) };
}

async function decryptSecretKey(ct: string, iv: string, salt: string, prfOutput: ArrayBuffer): Promise<Uint8Array> {
  const aesKey = await deriveAesKey(prfOutput, fromBase64(salt));
  const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: toBuffer(fromBase64(iv)) }, aesKey, toBuffer(fromBase64(ct)));
  const hex = new TextDecoder().decode(plaintext);
  return hexToBytes(hex);
}

// --- RP config ---

function rpConfig() {
  return { name: "litStrudel", id: location.hostname };
}

// --- Registration ---

export async function registerPasskey(secretKey: Uint8Array, pubkey: string, label: string): Promise<PasskeyRegistration> {
  const userId = new TextEncoder().encode(pubkey.slice(0, 32));

  const credential = (await navigator.credentials.create({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rp: rpConfig(),
      user: {
        id: userId,
        name: `litStrudel (${pubkey.slice(0, 8)}...)`,
        displayName: label,
      },
      pubKeyCredParams: [
        { alg: -7, type: "public-key" }, // ES256
        { alg: -257, type: "public-key" }, // RS256
      ],
      authenticatorSelection: {
        residentKey: "required",
        requireResidentKey: true,
        userVerification: "required",
      },
      extensions: {
        prf: { eval: { first: toBuffer(PRF_SALT) } },
      },
    },
  })) as PublicKeyCredential | null;

  if (!credential) throw new Error("Passkey creation cancelled");

  const response = credential.getClientExtensionResults();
  if (!response.prf?.enabled) {
    throw new Error("Your browser doesn't support passkey encryption (PRF). Try Safari 18+, Chrome 132+, or Bitwarden.");
  }

  // PRF was enabled but we need to do a get() to actually receive the PRF output
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rpId: rpConfig().id,
      allowCredentials: [{ id: credential.rawId, type: "public-key" }],
      userVerification: "required",
      extensions: {
        prf: { eval: { first: toBuffer(PRF_SALT) } },
      },
    },
  })) as PublicKeyCredential | null;

  if (!assertion) throw new Error("Passkey authentication cancelled");

  const assertionResults = assertion.getClientExtensionResults();
  const prfOutput = assertionResults.prf?.results?.first;
  if (!prfOutput) throw new Error("Failed to get encryption key from passkey");

  const { ct, iv, salt } = await encryptSecretKey(secretKey, toBuffer(prfOutput));
  const credentialId = toBase64Url(credential.rawId);

  const reg: PasskeyRegistration = {
    pubkey,
    credentialId,
    encryptedKey: ct,
    iv,
    salt,
    label,
    createdAt: new Date().toISOString(),
  };

  savePasskeyRegistration(reg);
  return reg;
}

// --- Unlock with specific credential ---

export async function unlockPasskey(reg: PasskeyRegistration): Promise<Uint8Array> {
  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rpId: rpConfig().id,
      allowCredentials: [{ id: toBuffer(fromBase64Url(reg.credentialId)), type: "public-key" }],
      userVerification: "required",
      extensions: {
        prf: { eval: { first: toBuffer(PRF_SALT) } },
      },
    },
  })) as PublicKeyCredential | null;

  if (!credential) throw new Error("Passkey authentication cancelled");

  const results = credential.getClientExtensionResults();
  const prfOutput = results.prf?.results?.first;
  if (!prfOutput) throw new Error("Failed to get encryption key from passkey");

  return decryptSecretKey(reg.encryptedKey, reg.iv, reg.salt, toBuffer(prfOutput));
}

// --- Discoverable unlock (empty allowCredentials) ---

export async function unlockAnyPasskey(): Promise<{ secretKey: Uint8Array; pubkey: string }> {
  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rpId: rpConfig().id,
      allowCredentials: [],
      userVerification: "required",
      extensions: {
        prf: { eval: { first: toBuffer(PRF_SALT) } },
      },
    },
  })) as PublicKeyCredential | null;

  if (!credential) throw new Error("Passkey authentication cancelled");

  const credentialId = toBase64Url(credential.rawId);
  const reg = getPasskeyRegistrations().find((r) => r.credentialId === credentialId);
  if (!reg) throw new Error("No matching passkey found in storage");

  const results = credential.getClientExtensionResults();
  const prfOutput = results.prf?.results?.first;
  if (!prfOutput) throw new Error("Failed to get encryption key from passkey");

  const secretKey = await decryptSecretKey(reg.encryptedKey, reg.iv, reg.salt, toBuffer(prfOutput));
  return { secretKey, pubkey: reg.pubkey };
}
